import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Table, Modal, Badge, Alert, Tab, Tabs, Spinner } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, addDoc, query, where, orderBy, serverTimestamp } from 'firebase/firestore';
import programAssignmentService from '../services/programAssignmentService';

const CourseBuilder = () => {
  const { currentUser, userProfile } = useAuth();
  const [showAddModuleModal, setShowAddModuleModal] = useState(false);
  const [showAddLessonModal, setShowAddLessonModal] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [modules, setModules] = useState([]);
  const [assignedPrograms, setAssignedPrograms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [newModule, setNewModule] = useState({
    moduleName: '',
    description: '',
    startDate: '',
    endDate: '',
    difficulty: 'Beginner',
    estimatedHours: 0,
    prerequisites: [],
    programId: ''
  });
  const [newLesson, setNewLesson] = useState({
    title: '',
    duration: '',
    type: 'video',
    content: '',
    scheduledDate: '',
    facilitator: '',
    notesReleaseDelay: 5 // minutes after scheduled time
  });

  useEffect(() => {
    if (currentUser && (userProfile?.role === 'facilitator' || userProfile?.role === 'admin')) {
      loadData();
    }
  }, [currentUser, userProfile]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load assigned programs for facilitators
      let programs = [];
      if (userProfile?.role === 'facilitator') {
        programs = await programAssignmentService.getFacilitatorPrograms();
      } else if (userProfile?.role === 'admin') {
        // Admins can see all programs
        programs = await programAssignmentService.getAvailablePrograms();
      }
      setAssignedPrograms(programs);

      // Load modules
      let modulesQuery;
      if (userProfile?.role === 'facilitator') {
        // Facilitators see only their modules
        modulesQuery = query(
          collection(db, 'modules'),
          where('facilitatorId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );
      } else {
        // Admins see all modules
        modulesQuery = query(
          collection(db, 'modules'),
          orderBy('createdAt', 'desc')
        );
      }

      const modulesSnapshot = await getDocs(modulesQuery);
      const modulesData = modulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        lessons: [] // Will be populated as needed
      }));

      setModules(modulesData);

      // Load sessions (scheduled lessons)
      const sessionsQuery = query(
        collection(db, 'sessions'),
        orderBy('scheduledDate', 'asc')
      );
      const sessionsSnapshot = await getDocs(sessionsQuery);
      const sessionsData = sessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSessions(sessionsData);

    } catch (error) {
      devError('Error loading course data:', error);
      showAlert('Error loading course data', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const handleAddModule = async () => {
    try {
      if (!newModule.moduleName || !newModule.description || !newModule.programId) {
        showAlert('Please fill in all required fields including program selection', 'danger');
        return;
      }

      const moduleData = {
        title: newModule.moduleName,
        moduleName: newModule.moduleName, // Keep for backward compatibility
        description: newModule.description,
        startDate: newModule.startDate ? new Date(newModule.startDate) : null,
        endDate: newModule.endDate ? new Date(newModule.endDate) : null,
        difficulty: newModule.difficulty,
        estimatedHours: parseInt(newModule.estimatedHours) || 0,
        prerequisites: newModule.prerequisites || [],
        programId: newModule.programId,
        facilitatorId: currentUser.uid,
        status: 'draft',
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        updatedAt: serverTimestamp()
      };

      await addDoc(collection(db, 'modules'), moduleData);
      showAlert('Module created successfully!', 'success');
      setShowAddModuleModal(false);

      // Reset form
      setNewModule({
        moduleName: '',
        description: '',
        startDate: '',
        endDate: '',
        difficulty: 'Beginner',
        estimatedHours: 0,
        prerequisites: [],
        programId: ''
      });

      // Reload data
      await loadData();
    } catch (error) {
      devError('Error creating module:', error);
      showAlert('Error creating module: ' + error.message, 'danger');
    }
  };

  const handleAddLesson = () => {
    devLog('Adding new lesson to module:', selectedModule?.id, newLesson);
    // In a real implementation, this would save to the database
    setShowAddLessonModal(false);
    setNewLesson({
      title: '',
      duration: '',
      type: 'video',
      content: '',
      scheduledDate: '',
      facilitator: '',
      notesReleaseDelay: 5
    });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'success';
      case 'Intermediate': return 'warning';
      case 'Advanced': return 'danger';
      default: return 'secondary';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'video': return 'primary';
      case 'project': return 'warning';
      case 'reading': return 'info';
      case 'assessment': return 'danger';
      default: return 'secondary';
    }
  };

  const getScheduledLessons = (moduleId) => {
    return sessions.filter(session => session.moduleId === moduleId);
  };

  if (userProfile?.role !== 'facilitator' && userProfile?.role !== 'admin') {
    return (
      <Alert variant="danger">
        <h5>Access Denied</h5>
        <p>Only facilitators and administrators can access the course builder.</p>
      </Alert>
    );
  }

  if (loading) {
    return (
      <div className="text-center my-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading course data...</p>
      </div>
    );
  }

  return (
    <div className="course-builder">
      {alert.show && (
        <Alert variant={alert.type} className="mb-3">
          {alert.message}
        </Alert>
      )}
      <Tabs defaultActiveKey="overview" id="course-builder-tabs" className="mb-4">
        <Tab eventKey="overview" title="Course Overview">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Course Modules</h5>
              <Button variant="primary" onClick={() => setShowAddModuleModal(true)}>
                + Add New Module
              </Button>
            </Card.Header>
            <Card.Body>
              {modules.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted">No modules created yet.</p>
                  {assignedPrograms.length > 0 ? (
                    <Button variant="primary" onClick={() => setShowAddModuleModal(true)}>
                      Create Your First Module
                    </Button>
                  ) : (
                    <Alert variant="info" className="mt-3">
                      You need to be assigned to a program before creating modules. Contact your administrator.
                    </Alert>
                  )}
                </div>
              ) : (
                <Row>
                  {modules.map(module => (
                  <Col md={6} lg={4} key={module.id} className="mb-4">
                    <Card className="h-100">
                      <Card.Header>
                        <div className="d-flex justify-content-between align-items-start">
                          <h6 className="mb-0">{module.moduleName}</h6>
                          <Badge bg={getDifficultyColor(module.difficulty)}>
                            {module.difficulty}
                          </Badge>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        <p className="text-muted small">{module.description}</p>
                        <div className="mb-2">
                          <small className="text-muted">
                            <strong>Duration:</strong> {module.estimatedHours} hours
                          </small>
                        </div>
                        <div className="mb-2">
                          <small className="text-muted">
                            <strong>Lessons:</strong> {module.lessons?.length || 0}
                          </small>
                        </div>
                        <div className="mb-2">
                          <small className="text-muted">
                            <strong>Scheduled:</strong> {getScheduledLessons(module.id).length} sessions
                          </small>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted">
                            <strong>Period:</strong> {
                              module.startDate && module.endDate ?
                                `${new Date(module.startDate.toDate ? module.startDate.toDate() : module.startDate).toLocaleDateString()} - ${new Date(module.endDate.toDate ? module.endDate.toDate() : module.endDate).toLocaleDateString()}` :
                                'Not set'
                            }
                          </small>
                        </div>
                        <div className="d-grid gap-2">
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            onClick={() => {
                              setSelectedModule(module);
                              setShowAddLessonModal(true);
                            }}
                          >
                            + Add Lesson
                          </Button>
                          <Button variant="outline-secondary" size="sm">
                            Edit Module
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  ))}
                </Row>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="lessons" title="Lesson Management">
          <Card>
            <Card.Header>
              <h5 className="mb-0">All Lessons</h5>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Module</th>
                    <th>Lesson</th>
                    <th>Type</th>
                    <th>Duration</th>
                    <th>Scheduled Date</th>
                    <th>Facilitator</th>
                    <th>Notes Available</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {modules.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        <p className="text-muted">No modules or lessons found.</p>
                      </td>
                    </tr>
                  ) : (
                    modules.map(module =>
                      (module.lessons || []).map(lesson => {
                      const scheduledLesson = getScheduledLessons(module.id).find(s => s.lessonId === lesson.id);
                      return (
                        <tr key={lesson.id}>
                          <td>
                            <small className="text-muted">Module {module.id}</small>
                            <br />
                            <strong>{module.moduleName}</strong>
                          </td>
                          <td>
                            <strong>{lesson.title}</strong>
                            <br />
                            <small className="text-muted">{lesson.content}</small>
                          </td>
                          <td>
                            <Badge bg={getTypeColor(lesson.type)}>
                              {lesson.type}
                            </Badge>
                          </td>
                          <td>{lesson.duration}</td>
                          <td>
                            {scheduledLesson ? (
                              <div>
                                {new Date(scheduledLesson.scheduledDate).toLocaleDateString()}
                                <br />
                                <small className="text-muted">
                                  {new Date(scheduledLesson.scheduledDate).toLocaleTimeString()}
                                </small>
                              </div>
                            ) : (
                              <Badge bg="warning">Not Scheduled</Badge>
                            )}
                          </td>
                          <td>
                            {scheduledLesson ? scheduledLesson.facilitator : 'TBD'}
                          </td>
                          <td>
                            {scheduledLesson ? (
                              <Badge bg="info">
                                5 min after start
                              </Badge>
                            ) : (
                              'N/A'
                            )}
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-1">
                              <Button variant="outline-primary" size="sm">
                                Edit
                              </Button>
                              {!scheduledLesson && (
                                <Button variant="outline-success" size="sm">
                                  Schedule
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                        );
                      })
                    )
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="schedule" title="Schedule Management">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Lesson Schedule</h5>
            </Card.Header>
            <Card.Body>
              <Alert variant="info">
                <h6>📋 Schedule Guidelines</h6>
                <ul className="mb-0">
                  <li>Lesson notes automatically become available 5 minutes after scheduled start time</li>
                  <li>Microsoft Teams links are generated automatically for each scheduled lesson</li>
                  <li>Facilitators can adjust the notes release delay if needed</li>
                  <li>Students receive notifications 24 hours and 1 hour before each lesson</li>
                </ul>
              </Alert>

              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Lesson</th>
                    <th>Scheduled Date/Time</th>
                    <th>Facilitator</th>
                    <th>Teams Link</th>
                    <th>Notes Release</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions
                    .sort((a, b) => {
                      const aDate = a.scheduledDate?.toDate ? a.scheduledDate.toDate() : new Date(a.scheduledDate);
                      const bDate = b.scheduledDate?.toDate ? b.scheduledDate.toDate() : new Date(b.scheduledDate);
                      return aDate - bDate;
                    })
                    .map(lesson => (
                      <tr key={lesson.id}>
                        <td>
                          <strong>{lesson.title}</strong>
                          <br />
                          <small className="text-muted">Module {lesson.moduleId}</small>
                        </td>
                        <td>
                          {new Date(lesson.scheduledDate).toLocaleDateString()}
                          <br />
                          <small className="text-primary">
                            {new Date(lesson.scheduledDate).toLocaleTimeString()}
                          </small>
                        </td>
                        <td>{lesson.facilitator}</td>
                        <td>
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            onClick={() => window.open(lesson.teamsLink, '_blank')}
                          >
                            🔗 Join
                          </Button>
                        </td>
                        <td>
                          <small className="text-muted">
                            {new Date(lesson.notesReleaseTime).toLocaleTimeString()}
                            <br />
                            (5 min after start)
                          </small>
                        </td>
                        <td>
                          <Badge bg={lesson.status === 'scheduled' ? 'success' : 'secondary'}>
                            {lesson.status}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            <Button variant="outline-primary" size="sm">
                              Edit
                            </Button>
                            <Button variant="outline-danger" size="sm">
                              Cancel
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Add Module Modal */}
      <Modal show={showAddModuleModal} onHide={() => setShowAddModuleModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Module</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Module Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={newModule.moduleName}
                    onChange={(e) => setNewModule({...newModule, moduleName: e.target.value})}
                    placeholder="Enter module name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Difficulty Level</Form.Label>
                  <Form.Select
                    value={newModule.difficulty}
                    onChange={(e) => setNewModule({...newModule, difficulty: e.target.value})}
                  >
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Assigned Program *</Form.Label>
              <Form.Select
                value={newModule.programId}
                onChange={(e) => setNewModule({...newModule, programId: e.target.value})}
                required
              >
                <option value="">Select a program</option>
                {assignedPrograms.map(program => (
                  <option key={program.id} value={program.id}>
                    {program.name} ({program.category})
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                {assignedPrograms.length === 0
                  ? 'No programs assigned. Contact administrator.'
                  : 'Module will be created under this program'}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newModule.description}
                onChange={(e) => setNewModule({...newModule, description: e.target.value})}
                placeholder="Enter module description"
              />
            </Form.Group>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newModule.startDate}
                    onChange={(e) => setNewModule({...newModule, startDate: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newModule.endDate}
                    onChange={(e) => setNewModule({...newModule, endDate: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Estimated Hours</Form.Label>
                  <Form.Control
                    type="number"
                    value={newModule.estimatedHours}
                    onChange={(e) => setNewModule({...newModule, estimatedHours: parseInt(e.target.value)})}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModuleModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddModule}>
            Create Module
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Lesson Modal */}
      <Modal show={showAddLessonModal} onHide={() => setShowAddLessonModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Lesson to {selectedModule?.moduleName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Lesson Title</Form.Label>
                  <Form.Control
                    type="text"
                    value={newLesson.title}
                    onChange={(e) => setNewLesson({...newLesson, title: e.target.value})}
                    placeholder="Enter lesson title"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Type</Form.Label>
                  <Form.Select
                    value={newLesson.type}
                    onChange={(e) => setNewLesson({...newLesson, type: e.target.value})}
                  >
                    <option value="video">Video</option>
                    <option value="project">Project</option>
                    <option value="reading">Reading</option>
                    <option value="assessment">Assessment</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Duration</Form.Label>
                  <Form.Control
                    type="text"
                    value={newLesson.duration}
                    onChange={(e) => setNewLesson({...newLesson, duration: e.target.value})}
                    placeholder="e.g., 2 hours"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Content Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newLesson.content}
                onChange={(e) => setNewLesson({...newLesson, content: e.target.value})}
                placeholder="Enter lesson content description"
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Scheduled Date & Time</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={newLesson.scheduledDate}
                    onChange={(e) => setNewLesson({...newLesson, scheduledDate: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Facilitator</Form.Label>
                  <Form.Control
                    type="text"
                    value={newLesson.facilitator}
                    onChange={(e) => setNewLesson({...newLesson, facilitator: e.target.value})}
                    placeholder="Enter facilitator name"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Notes Release Delay (minutes after lesson start)</Form.Label>
              <Form.Control
                type="number"
                value={newLesson.notesReleaseDelay}
                onChange={(e) => setNewLesson({...newLesson, notesReleaseDelay: parseInt(e.target.value)})}
                min="0"
                max="30"
              />
              <Form.Text className="text-muted">
                Default is 5 minutes. This allows all participants to join before accessing materials.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddLessonModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddLesson}>
            Add Lesson
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CourseBuilder;